import { useCallback, useRef, type AnchorHTMLAttributes, type ReactNode } from 'react'
import { formatFieldValue, formatMoneyField, isTypedBlank } from '@solum/format'
import { Input } from '../controls/Input'
import { Textarea } from '../controls/Textarea'
import { FieldActions, permittedActions, type FieldAction } from './FieldAction'
import { Select } from '../controls/Select'
import { Switch } from '../controls/Switch'
import { DatePicker } from '../controls/DatePicker'
import { MultiSelect, type MSOption } from '../controls/MultiSelect'
import { optionLabel } from '../controls/optionSet'
import type { Tone } from '../controls/Pill'
import s from './FieldEditor.module.css'

/**
 * What a `multiselect` holds, whatever the transport made of a `text[]`.
 *
 * `MultiSelect` takes `string[]` and nothing else — hand it `undefined` and it
 * throws on `.map`, hand it a string and it spreads into characters. Three
 * shapes actually arrive here and each is a real case rather than defensive
 * padding:
 *
 *   · `string[]`   — PostgREST returns a `text[]` column as a JSON array.
 *   · `undefined`  — a CREATE form before anything is chosen. The column is
 *                    `not null default '{}'`, so absence means the empty set
 *                    and not "unknown".
 *   · anything else — a fixture or a mis-registered column. Coerced to empty
 *                    rather than crashing the whole record page: the field
 *                    renders as unset, which is wrong in a visible, recoverable
 *                    way, where a throw takes every other field down with it.
 *
 * Non-string members are dropped rather than stringified. A stored key is a
 * key; `String(null)` would put the word "null" in a chip and then try to
 * resolve it against the catalogue.
 */
export const selectedValues = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : []

/** A navigable read-only value remains visually recognisable as a field. */
export function ReadOnlyLinkField({ children, ...link }: AnchorHTMLAttributes<HTMLAnchorElement>) {
  return (
    <span className={s.readOnlyLinkField} data-readonly-link-field>
      <a className={s.link} {...link}>{children}</a>
    </span>
  )
}

/**
 * ONE control per field kind (#1679 slice 2) — the renderer half.
 *
 * `apps/console/src/form/EntityForm.tsx` and `apps/console/src/crm/RecordPage.tsx`
 * are both generic renderers over `core.field_meta.kind`, and on `origin/main`
 * they are kept in step by a SOURCE-TEXT test: `optionSet.test.tsx:242-271`
 * reads both files and greps them. Its own comment says the property that
 * matters is that "both go through `resolveOption`… neither may print the
 * stored key". A grep is a reasonable way to hold that when there is nothing
 * shared to point at. This is the something to point at.
 *
 * ── THE BINDING SPLIT IS DECLARED, NOT HIDDEN ───────────────────────────────
 *
 * `EntityForm` uses react-hook-form, and the split between `register(...)` and
 * `<Controller>` in it is not arbitrary — measured, it falls exactly where the
 * leaf changes shape:
 *
 *   · `register` where the leaf is a native `<input>`. `Input` is a forwardRef
 *     passthrough, so RHF's ref lands on the real DOM node and the field stays
 *     UNCONTROLLED.
 *   · `<Controller>` where the leaf is a Radix or custom component that accepts
 *     no ref and no DOM change event — `Select` (`onValueChange`), `Switch`
 *     (`onCheckedChange`), `DatePicker` (`onChange: (value: string) => void`) —
 *     plus the read-only `option`, which has to READ the value to resolve it.
 *
 * A registry that returned an element and quietly wrapped everything in
 * `Controller` would turn four uncontrolled fields into controlled ones: a
 * re-render per keystroke, different `reset`/`defaultValues` semantics, and a
 * changed SSR shape that `EntityForm.test.tsx:73-79` counts literally. So the
 * binding is a QUESTION this module answers (`fieldEditorBinding`) and the host
 * still owns its own form library. Nothing here imports react-hook-form.
 *
 * ── WHAT THE HOST STILL OWNS ────────────────────────────────────────────────
 *
 * A `ref` needs to know what the column points at, which is a metamodel read;
 * `EntityForm` stays free of data hooks so the fixture surfaces can render it
 * with no server. So `ref` renders the host's `slot`, and falls back to the raw
 * input when the host offers none — "the honest answer for a ref the metamodel
 * does not describe", as `EntityForm.tsx:293-298` puts it.
 */

export interface FieldEditorField {
  kind: string
  /** `core.field_meta.format`; unlike `kind`, this changes presentation only. */
  format?: string | null
  /** the form key — a `name` on the control, and what the host binds */
  name: string
  label: string
  options?: readonly { value: string; label: string; tone?: Tone; selectable?: boolean }[]
  required?: boolean
  /** `core.field_meta.read_only` for this column */
  readOnly?: boolean
  /**
   * `core.field_meta.rules`, as far as the EDITOR needs it.
   *
   * Structural, not imported: `packages/ui` does not depend on
   * `@solum/experience`, and a control that reached for a schema package to
   * learn its own shape would invert the layering. Only the keys this file
   * acts on are named, so a host passing the whole `rules` object is fine and
   * an unknown key is ignored rather than guessed at.
   */
  rules?: { multiline?: boolean }
}

export type FieldEditorBinding = 'native' | 'controlled'

/**
 * Which binding this field needs — ask before rendering, so the host can wrap
 * a controlled kind in whatever its form library calls a controller and spread
 * its own registration onto a native one.
 *
 * `formReadOnly` outranks `field.readOnly`: the door is shut for the whole
 * table, so an "editable" column is still not the reader's to change
 * (`EntityForm.tsx:254-256`).
 */
export function fieldEditorBinding(
  field: Pick<FieldEditorField, 'kind' | 'format' | 'readOnly'>,
  formReadOnly = false,
): FieldEditorBinding {
  const readOnly = field.readOnly || formReadOnly
  // Typed read-only presentation needs the value before a native registration
  // has mounted: option/multiselect resolve vocabulary, ref delegates to the
  // host's metamodel-aware slot, URL validates before linking, and datetime
  // keeps an exact machine value beside a human rendering, and money formats
  // the value in its record's currency (#2757 R4.1).
  if (readOnly) return ['option', 'multiselect', 'ref', 'datetime', 'money'].includes(field.kind)
    || field.format === 'url' ? 'controlled' : 'native'
  return ['option', 'boolean', 'ref', 'date', 'multiselect'].includes(field.kind) ? 'controlled' : 'native'
}

/** A link is offered only for an absolute HTTP(S) URL. */
export function safeHttpUrl(value: unknown): string | null {
  if (typeof value !== 'string' || value === '') return null
  try {
    const url = new URL(value)
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.href : null
  } catch {
    return null
  }
}

/** Human presentation only; the exact instant remains on the `<time>`. */
export function readableDateTime(value: unknown): string | null {
  if (typeof value !== 'string' || value === '') return null
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return null
  /* The registry's one datetime convention (#3011), so a timestamp reads the
     same on the record as in its grid — and no longer depends on the host's
     locale data (the `undefined` locale rendered differently per machine). */
  return formatFieldValue('datetime', value)
}

export interface FieldEditorProps {
  field: FieldEditorField
  /** the whole form is read-only — a shut write door, not a per-column rule */
  readOnly?: boolean
  /** `native` binding: the host's registration, spread onto the input */
  native?: Record<string, unknown>
  /** `controlled` binding */
  value?: unknown
  onChange?: (next: unknown) => void
  onBlur?: () => void
  /** an id the host has already pointed a `<label htmlFor>` at */
  id?: string
  /** the host's own control for this kind — used for `ref`, which needs a metamodel read */
  slot?: ReactNode
  /** overrides the derived placeholder — the server-default case */
  placeholder?: string
  /** the RECORD's own ISO 4217 code, for a `money` field (#2757 R4.1); else the field's static `format` */
  currency?: string | null
  /**
   * What `Field` clones onto its single child, forwarded to the LEAF.
   *
   * This is not optional politeness. `Field` clones `id` / `aria-describedby` /
   * `aria-invalid` / `aria-required` onto whatever single element it wraps, and
   * the moment that element became `<FieldEditor>` instead of `<Input>` the
   * clone landed on a component that dropped it — so every field lost its
   * required/invalid/description wiring at once. Caught by
   * `EntityForm.serverDefault.test.tsx`'s `aria-required` assertion, which is
   * the reason that test is worth more than it looks.
   */
  'aria-describedby'?: string
  'aria-invalid'?: boolean
  'aria-required'?: boolean
  /**
   * Optional ACCESSORY offers for a typed text or multiline editor (#1679).
   *
   * Not `slot`, and the distinction is the ruling's: `slot` REPLACES the
   * control for `ref`, and overloading it to also decorate a text field would
   * make a missing slot capable of erasing the real editor. These sit BESIDE
   * the editor and hand the host the staged value and the caret.
   *
   * Absent — or present but not permitted — nothing renders and the markup,
   * focus order and binding are byte-for-byte what they were.
   */
  actions?: readonly FieldAction[]
  /** What the host permits. `null` = the host has no capability model. */
  permittedCapabilities?: readonly string[] | null
  /**
   * Write the next value through the HOST's binding.
   *
   * Required for an action to do anything, and separate from `onChange`
   * deliberately: `onChange` is the controlled-binding channel, while a native
   * text field is bound by the host's own `register()` and has no `onChange`
   * here at all. The host writes with whatever keeps ITS dirty and validation
   * state honest — in `EntityForm` that is
   * `setValue(name, v, { shouldDirty: true, shouldValidate: true })`.
   */
  onActionCommit?: (next: string) => void
}

export function FieldEditor({
  field, readOnly = false, native, value, onChange, onBlur, id, slot, placeholder, currency,
  actions, permittedCapabilities, onActionCommit, ...aria
}: FieldEditorProps): ReactNode {
  /* A ref on the leaf, which this component has never held.
     The host's `register()` puts its OWN ref inside `native`, and this file
     needs one too, to read `selectionStart` for an action.

     ── REACT DOES NOT MERGE REFS, AND THIS ASSUMED IT DID ──────────────────
     This was written as `{...native} ref={leafRef}` with a comment saying "the
     later prop wins for the DOM attribute, but React merges refs, so both are
     called". It does not, and there is no order that works: `ref` is an
     ordinary prop, so whichever comes last simply replaces the other.

     Measured, by rendering `EntityForm` over an existing row:

       {...native} ref={leafRef}   ->  leafRef wins. `register()`'s ref is
                                       dropped, so react-hook-form never
                                       attaches the field and `defaultValues`
                                       never reach the DOM. **Every native-bound
                                       text field renders EMPTY on an existing
                                       record** — measured "" for a row holding
                                       "Hello".
       ref={leafRef} {...native}   ->  the host's ref wins, the row appears, and
                                       `leafRef.current` is null — so
                                       `readStaged` returns null and no action
                                       can ever read the caret.

     So both refs are attached by hand. One callback, both callees, and it
     handles a callback ref and an object ref because `register()` returns the
     first and a plain `useRef` host would pass the second. */
  const leafRef = useRef<HTMLInputElement & HTMLTextAreaElement>(null)
  /* The host's ref is BOXED, and the callback below depends on nothing.
     `register()` returns a fresh closure every render, so a `useCallback`
     keyed on it memoises nothing: React sees a new ref callback each time and
     detaches then reattaches, calling the host's ref with `null` and then the
     element on EVERY render. Measured — `setLeaf` calls at mount were
     `["el", "null", "el"]` across two renders. No defect was reproducible from
     it (a differential probe typed a value and forced two re-renders: same DOM
     node, same value, both with and without the churn), so this is a quality
     fix rather than a second bug — but a stable callback costs one ref and
     removes the whole question. */
  const hostRefBox = useRef<unknown>(null)
  hostRefBox.current = (native as { ref?: unknown } | undefined)?.ref
  const setLeaf = useCallback((el: (HTMLInputElement & HTMLTextAreaElement) | null) => {
    leafRef.current = el
    const host = hostRefBox.current
    if (typeof host === 'function') (host as (n: unknown) => void)(el)
    else if (host && typeof host === 'object') {
      (host as { current: unknown }).current = el
    }
  }, [])
  const readStaged = useCallback(() => {
    const el = leafRef.current
    if (!el) return null
    /* The STAGED value, read off the DOM rather than from props. A native-bound
       field is uncontrolled — the host has no idea what has been typed until
       the form library reads it — so props would hand the action the value as
       LOADED and silently discard everything since. */
    return {
      value: el.value,
      selection: { start: el.selectionStart ?? el.value.length, end: el.selectionEnd ?? el.value.length },
    }
  }, [])
  const { kind, name, label, options = [] } = field
  const shut = field.readOnly || readOnly
  /* Forwarded to whichever leaf this kind renders. `id` travels with them so a
     host's `<label htmlFor>` resolves against the control and not against the
     wrapper. */
  const wiring = { id, ...aria }

  if (shut) {
    if (kind === 'option') {
      /* A read-only option is READ-ONLY, not absent. Rendering the value alone
         gave the registered label but took away a focusable, labelled control:
         a keyboard user could no longer reach the field and a screen reader
         announced loose text. So the resolved LABEL goes into the same
         read-only input every other kind uses. `<Input readOnly>` would print
         the stored key — `sole_trader` where the catalogue says "Sole trader" —
         which is storage vocabulary shown as ordinary presentation. */
      return (
        <Input
          readOnly
          value={optionLabel(value, options)}
          aria-label={label}
          data-testid={`opt-ro-${name}`}
          {...wiring}
        />
      )
    }
    if (kind === 'multiselect') {
      /* Same argument as the read-only `option` directly above, one cardinality
         out: a read-only multiselect is READ-ONLY, not absent, so it keeps a
         focusable labelled control — and it must print the registered LABELS,
         never the stored keys. Handing the raw value straight to a read-only
         Input would render a Postgres text[] as the driver's own string
         ("ted,tenderned"): storage vocabulary shown as presentation, twice over.

         That sentence originally quoted the offending JSX literally, and
         `optionSet.test.tsx:284` — a SOURCE-TEXT lint that greps this file for
         exactly that shape — matched the COMMENT and failed. The code was
         correct; the prose tripped the check. Worth recording rather than just
         reworded: it is rule 26 from the inside, a lint matching a token rather
         than the property, and the cheapest fix is not to write the forbidden
         shape in a file that is read as text. */
      return (
        <Input
          readOnly
          value={selectedValues(value).map((v) => optionLabel(v, options)).join(', ')}
          aria-label={label}
          data-testid={`ms-ro-${name}`}
          {...wiring}
        />
      )
    }
    if (kind === 'ref') {
      // A reference without a declared/visible target fails closed. A UUID is
      // storage identity, never the read-only presentation fallback.
      return <>{slot ?? <span role="status" className={s.unavailable} {...wiring}>Record unavailable</span>}</>
    }
    if (field.format === 'url') {
      const visible = typeof value === 'string' ? value : ''
      const href = safeHttpUrl(visible)
      return href
        ? <ReadOnlyLinkField href={href} target="_blank" rel="noopener noreferrer" {...wiring}>{visible}</ReadOnlyLinkField>
        : <Input readOnly value={visible} aria-label={label} {...wiring} />
    }
    if (kind === 'datetime') {
      const exact = typeof value === 'string' ? value : ''
      const readable = readableDateTime(exact)
      return readable
        ? <time className={s.readOnlyValue} dateTime={exact} title={exact} {...wiring}>{readable}</time>
        : <Input readOnly value={exact} aria-label={label} {...wiring} />
    }
    if (kind === 'money') {
      // Currency, sign and every stored decimal survive (#2757 R4.1); the
      // record's own currency first, else the field's static ISO code.
      return <Input readOnly value={formatMoneyField(value, currency ?? field.format ?? null)} aria-label={label} {...wiring} />
    }
    return <Input {...native} readOnly {...wiring} />
  }

  if (kind === 'option') {
    /* The accessible name is the FIELD's label. A placeholder is a hint about
       what to do, not the name of the control — and `Field` clones an `id` onto
       its single child, which for a controlled kind is the host's controller, a
       render-prop component that forwards none of it. So `aria-label` is not
       competing with a real <label>; it IS the name. */
    return (
      <Select
        value={typeof value === 'string' ? value : ''}
        onValueChange={(next) => onChange?.(next)}
        options={[...options]}
        aria-label={label}
        placeholder={placeholder ?? `Select ${label.toLocaleLowerCase()}…`}
        {...wiring}
      />
    )
  }

  if (kind === 'multiselect') {
    /* The chip-combobox, on the SHARED path (#1928).

       `crm/RecordPage.tsx` has had this arm since the control shipped; the
       generic `/e/$entity/$id` path — `RecordBody` → `EntityForm` → here — did
       not, so a multiselect column reaching it fell through to the text
       fallthrough at the foot of this file and rendered a Postgres array in a
       one-line input. The two generic renderers disagreed about what a field
       IS, which is the exact defect this module was created to end, and the
       ruling (mailbox #1958 comment 61687) closes BOTH paths rather than
       leaving `/e/` a diagnostic.

       `testid` matches the CRM arm's `ms-${field}` so one browser spec can
       address the control on either host. The value is normalised through
       `selectedValues` because what arrives here is whatever the transport made
       of a `text[]` — an array from PostgREST, and `undefined` on a create form
       before anything is chosen. */
    return (
      <MultiSelect
        value={selectedValues(value)}
        options={options as MSOption[]}
        onChange={(next) => onChange?.(next)}
        testid={`ms-${name}`}
        placeholder={placeholder ?? `Add ${label.toLocaleLowerCase()}…`}
        aria-label={label}
        {...wiring}
      />
    )
  }

  if (kind === 'boolean') {
    /* The accessible name has to be stated here for the same reason as `option`:
       a Radix Switch receives nothing from `Field`'s clone. Measured on
       `origin/main`, `EntityForm` passed none, so every boolean field on every
       generic record page reached a screen reader unnamed. */
    return (
      <Switch
        checked={value === true}
        onCheckedChange={(next) => onChange?.(next)}
        aria-label={label}
        {...wiring}
      />
    )
  }

  if (kind === 'ref') {
    // No picker offered ⇒ the raw input, which is the honest answer for a ref
    // the metamodel does not describe.
    return <>{slot ?? <Input {...native} className={s.mono} {...wiring} />}</>
  }

  if (kind === 'date') {
    return (
      <DatePicker
        id={id}
        name={name}
        value={typeof value === 'string' ? value : ''}
        required={field.required}
        onChange={(next) => onChange?.(next)}
        onBlur={onBlur}
      />
    )
  }

  /* Everything else is a native input. `inputMode="decimal"` rather than
     `type="number"` on the numeric kinds, deliberately and unchanged: a
     `type="number"` input hands back `''` for anything it considers invalid, so
     the keystrokes are gone before any validator sees them — which is the same
     defect `@solum/format`'s `parseFieldValue` exists to stop downstream. */
  const numeric = kind === 'money' || kind === 'number' || kind === 'percent'

  /* MULTILINE, and it is config — never a kind.
     `core.field_meta.rules.multiline` has been set by 25 registrations across
     five modules and core since they shipped: compliance policy bodies, ISMS
     scope statements, corrective actions, internal-audit findings, tender
     answers. Nothing read it, so a policy body was typed into a one-line box.
     Gating on the CONFIG rather than on the arm matters, because this arm is
     the FALLTHROUGH — it catches text, uuid, json, country, email, phone,
     datetime, money, number, percent and every unknown kind. A branch keyed on
     "the text arm" would have made ten kinds multi-line at once.
     Numeric kinds are excluded on the same principle: `rules` describes a
     column, and a money column that carries a stale `multiline` should not
     become a paragraph box. */
  const multiline = field.rules?.multiline === true && !numeric

  /* The ACCESSORY seam. Nothing below this point runs unless the host both
     offered an action and can write the result — `permittedActions` returns
     empty for an unpermitted capability, and `FieldActions` renders `null` for
     an empty list, so a field that does not opt in emits exactly the markup it
     emitted before. */
  const offers = onActionCommit ? permittedActions(actions, permittedCapabilities) : []
  /* `ref={setLeaf}` LAST, and deliberately: it is the only ref that must win,
     because it is the one that calls the other. See `setLeaf` above. */
  const leaf = multiline ? <Textarea {...native} ref={setLeaf} placeholder={placeholder} {...wiring} />
    : <Input {...native} ref={setLeaf} type={field.format === 'url' ? 'url' : undefined}
             inputMode={numeric ? 'decimal' : undefined}
             placeholder={placeholder} {...wiring} />

  if (offers.length === 0) return leaf

  return (
    <>
      {leaf}
      <FieldActions
        actions={offers}
        permitted={permittedCapabilities}
        label={`${field.label} actions`}
        disabled={readOnly}
        read={readStaged}
        commit={(next, caret) => {
          onActionCommit?.(next)
          /* Put the caret back where the insertion ended, on the next frame —
             the host's write re-renders the leaf, and a caret restored before
             that is a caret the re-render discards. Without this an action
             leaves the cursor at position 0 and the next keystroke lands at the
             top of the document. */
          requestAnimationFrame(() => {
            const el = leafRef.current
            if (!el) return
            el.focus()
            const at = Math.max(0, Math.min(caret, el.value.length))
            el.setSelectionRange(at, at)
          })
        }}
      />
    </>
  )
}

/**
 * Re-exported so a caller can ask the value question and the control question
 * in one import. The value half is headless and lives in `@solum/format`; this
 * is the seam, not a second copy.
 */
export { isTypedBlank }
